import store from "@/store";
import { scaleLinear, min, forceSimulation, forceX, forceY, forceCollide, linkHorizontal, axisBottom } from "d3";
import { uniqBy } from "lodash";
import { deepCopy } from "../../services/helpers";
import { sourceSongs, targetSongs, ownSongs } from "../../services/filters";

class Timeline {
	constructor (artist) {

		this.width = 1332;
		this.height = 520;
    
		this.colors = {
			source: "#3aa1db",
			own: "#999999",
			target: "#fca82b",
			na: "#ffffff"
		};

		this.minYearLimit = 1900;
		this.maxYearLimit = 2015;
		this.nTicks = 130;
		this.past = {
			width: 200,
			margin: {
				left: 60,
				right: 10
			}
		};

		this.margin = {
			left: 0,
			top: 0,
			right: 0,
			bottom: 0
		};

		this.row = {
			height: this.height/3,
			margin: {
				top: 0,
				left: 0,
				bottom: 0,
				right: 0
			}
		};
    
		this.levels = [
			"source",
			"own",
			"target"
		];

		this.offset = 0;
		this.artist = artist;
        
		this.ownSongs = deepCopy(ownSongs(artist)).filter(s => s.year != "").map(s => { s.level = "own"; return s; });
		this.ownSongsIds = this.ownSongs.map(s => s.id);
		this.sourceSongs = deepCopy(uniqBy(sourceSongs(artist).map(s => store.state.songs[s.parent_id]).filter(s => !this.ownSongsIds.includes(s.id) && s.year != "").map(s => { s.level = "source"; return s; }), "id"));
		this.sourceSongIds = this.sourceSongs.map(s => s.id);
		this.targetSongs = deepCopy(targetSongs(artist)).filter(s => !this.ownSongsIds.includes(s.id) && !this.sourceSongIds.includes(s.id) && s.year != "").map(s => { s.level = "target"; return s; });

		// Combine all songs
		let allSongs = uniqBy(
			this.sourceSongs.concat(this.ownSongs).concat(this.targetSongs), 
			s => s.id
		).map(s => {
			s.radius = 5.5; // + s.n_children * 0.9;
			return s;
		});
    
		// Create a lookup object
		this.allSongs = {};
		allSongs.forEach(song => {
			this.allSongs[song.id] = song;
		});
    
		this.allSongsValues = Object.values(this.allSongs);
    
		this.allSongsIds = this.allSongsValues.map(s => s.id);
    
		// Found the new year limit
		this.minYearLimit = min(
			this.allSongsValues.filter(s => s.year >= this.minYearLimit)
				.map(s => parseInt(s.year))
		);
        
		// Generate the domain
		this.domain = [this.minYearLimit, this.maxYearLimit];
    
		// Generate the X Scale
		this.x = scaleLinear()
			.domain(this.domain)
			.range([this.past.width + 30, this.width - 10]);
              
		// X Axis
		this.axis = {
			x: axisBottom(this.x).ticks(6, "d")
		};
    
		// Generate links
		this.links = [];
    
		Object.values(this.allSongs).forEach(s => {
			if (this.allSongsIds.includes(s.parent_id)) {
				this.links.push({
					source: this.allSongs[s.parent_id],
					target: s
				});
			}
		});
        
		// Generate the 3 rows arrays
		this.rows = [
			this.makeRow(this.sourceSongs, -1),
			this.makeRow(this.ownSongs, 0),
			this.makeRow(this.targetSongs, 1)
		];

		this.diagonal = linkHorizontal()
			.x(d => d.x)
			.y(d => d.y);

		// Find all artists
		this.artists = {};

		this.allSongsValues.forEach(s => {
			["author", "interpret", "composer"].forEach(role => {
				s[`${role}_ids`].forEach(id => {
					this.addArtistData(id, s.level, role);
				});
			});
		});

		this.artists = Object.values(this.artists);
	} 

	addArtistData (id, level, role) {
		if (this.artists[id] === undefined) {
			this.artists[id] = {
				id: id,
				source: {
					composer: 0,
					interpret: 0,
					author: 0,   
					total: 0
				},
				own: {
					composer: 0,
					interpret: 0,
					author: 0,   
					total: 0
				},
				target: {
					composer: 0,
					interpret: 0,
					author: 0,   
					total: 0
				}
			};
		}

		this.artists[id][level][role]++;
		this.artists[id][level].total++;
	}

	makeRow (songs) {

		let simulation = forceSimulation(songs)
			.force("x", forceX(d => this.xScale(d)).strength(0.3))
			.force("y", forceY(this.offset + this.row.height / 2))
			.force("collide", forceCollide(d => d.radius))
			.stop();

		for (let i = 0; i < this.nTicks; ++i) simulation.tick();

		/*let voronois = voronoi()
            .extent([
                [-this.row.margin.left, this.offset -this.row.margin.top], 
                [this.width + this.row.margin.right, this.offset + this.row.height + this.row.margin.top]
            ])
            .x(d => d.x)
            .y(d => d.y)
            .polygons(songs) */

		this.offset += this.row.height;

		return {
			//voronois: voronois,
			nodes: songs
		};
	}
  
	xScale (d) {
		if (d.year < this.minYearLimit) {
			let domain = this.minYearLimit - 1500;
			return this.past.margin.left + (this.past.width - this.past.margin.left - this.past.margin.right) * (d.year - 1500) / domain;
		}

		return this.x(d.year);
	}
  
	color (level) {
		if (level == "source") {
			return this.colors.source;
		}
		if (level == "own") {
			return this.colors.own;
		}
		if (level == "target") {
			return this.colors.target;
		}
		return this.colors.na;
	}
}

export { Timeline };
